import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Modal,
  InputGroup,
  Card,
  Spinner,
} from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { FaEdit, FaTrash, FaFileExport, FaBoxOpen } from 'react-icons/fa';
import axios from 'axios';
import BASE_URL from '../config';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './AdminLayout.css';

// ********** IMPORT MAP COMPONENT **********
// Confirmed import from the separate file containing MapPopup component
import MapPopup from '../components/Map';
// ******************************************

// --- Configuration & Utilities ---

const defaultCenter = [24.4667, 54.3667];

// Axios instance (unchanged)
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Helper to format timestamps (unchanged)
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// ================= MAIN COMPONENT =================
const AdminVendor = () => {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    externalId: '',
    latitude: '',
    longitude: '',
    address: '',
  });
  const [showMapPopup, setShowMapPopup] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);

  // Map Language State - ADDED to pass to MapPopup
  const [mapLanguage, setMapLanguage] = useState('en');

  // Message modal state
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });

  // Helper functions for the message modal
  const showMessageModal = (title, body) => {
    setMessageModal({ show: true, title, body });
  };

  const handleCloseMessageModal = () => {
    setMessageModal({ show: false, title: '', body: '' });
  };

  // Fetch a single page of vendors
  const fetchVendorsPage = async (page, rowsPerPage) => {
    const params = {
      per_page: rowsPerPage,
    };

    const res = await authAxios.get(`/vendors?page=${page}`, { params });

    const responseData = res.data?.data;
    const vendorsData = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData)
      ? responseData
      : [];

    // Extract pagination metadata
    const totalCount = responseData?.total;
    const lastPageNum =
      responseData?.last_page ||
      responseData?.meta?.last_page ||
      res.data?.last_page;
    const currentPageNum =
      responseData?.current_page ||
      responseData?.meta?.current_page ||
      res.data?.current_page ||
      page;

    return {
      vendorsData,
      totalCount,
      lastPageNum,
      currentPageNum,
      hasMore: lastPageNum
        ? page < lastPageNum
        : vendorsData.length === rowsPerPage,
    };
  };

  // Fetch all vendors page by page until last page
  const fetchAllVendorsSequentially = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    setVendors([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let allVendorsList = [];
      let page = 1;
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get metadata
      const firstPageData = await fetchVendorsPage(page, perPage);
      allVendorsList = [...firstPageData.vendorsData];
      lastPageNum = firstPageData.lastPageNum;
      totalCount = firstPageData.totalCount || allVendorsList.length;

      // Update state with first page data
      setVendors(allVendorsList);
      setCurrentPage(page);
      setTotalRows(totalCount);
      if (lastPageNum) {
        setLastPage(lastPageNum);
      }

      // If we have last_page info, fetch remaining pages
      if (lastPageNum && lastPageNum > 1) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchVendorsPage(page, perPage);
            if (pageData.vendorsData.length === 0) {
              break;
            }
            allVendorsList = [...allVendorsList, ...pageData.vendorsData];
            setVendors([...allVendorsList]);
            setCurrentPage(page);
          } catch (err) {
            console.error(`Error fetching vendors page ${page}:`, err);
            break;
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchVendorsPage(page, perPage);
            if (pageData.vendorsData.length === 0) {
              break;
            }
            allVendorsList = [...allVendorsList, ...pageData.vendorsData];
            setVendors([...allVendorsList]);
            setCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.vendorsData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching vendors page ${page}:`, err);
            break;
          }
        }
      }

      setHasMore(false);
      setLastPage(page);
      setTotalRows(allVendorsList.length);
      console.log(
        `Finished fetching all vendors. Total: ${allVendorsList.length}`
      );
    } catch (err) {
      console.error('Error fetching all vendors:', err);
      setVendors([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchAllVendorsSequentially();
  }, []);

  const filteredVendors = useMemo(() => {
    const lowerCaseSearch = search.toLowerCase();
    if (!lowerCaseSearch) {
      return vendors;
    }
    return vendors.filter(
      (v) =>
        v.name?.toLowerCase().includes(lowerCaseSearch) ||
        v.email?.toLowerCase().includes(lowerCaseSearch) ||
        v.phone?.toLowerCase().includes(lowerCaseSearch) ||
        v.address?.toLowerCase().includes(lowerCaseSearch)
    );
  }, [vendors, search]);

  // Refresh vendors after create/update/delete
  const refreshVendors = () => {
    fetchAllVendorsSequentially();
  };

  // Removed Intersection Observer - now fetching all pages sequentially

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleOpenMap = () => {
    // Parse and check if current coordinates are valid to center the map
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    if (!isNaN(lat) && !isNaN(lng)) setMapCenter([lat, lng]);
    else setMapCenter(defaultCenter);

    setShowMapPopup(true);
  };

  const handleSelectLocation = (coordinates, address) => {
    // Update form data with coordinates and address from map
    // Coordinates passed from MapPopup are already strings with fixed precision
    setFormData({
      ...formData,
      latitude: coordinates[0],
      longitude: coordinates[1],
      address,
    });
    setShowMapPopup(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      // Ensure coordinates are sent as numbers or null
      lat: formData.latitude ? parseFloat(formData.latitude) : null,
      lng: formData.longitude ? parseFloat(formData.longitude) : null,
      external_id: formData.externalId,
    };
    try {
      if (editingVendor) {
        await authAxios.put(`/vendors/${editingVendor.id}`, payload);
        showMessageModal('Success', 'Vendor updated successfully.');
      } else {
        await authAxios.post('/vendors', payload);
        showMessageModal('Success', 'Vendor added successfully.');
      }
      // Reset and reload vendors from the beginning
      setVendors([]);
      setCurrentPage(1);
      setHasMore(true);
      refreshVendors();
      setShowModal(false);
      setEditingVendor(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        externalId: '',
        latitude: '',
        longitude: '',
        address: '',
      });
    } catch (error) {
      console.error('Failed to save vendor:', error);
      showMessageModal(
        'Error',
        `Failed to save vendor. Details: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await authAxios.delete(`/vendors/${id}`);
      showMessageModal('Success', 'Vendor deleted successfully.');
      // Reset and reload vendors from the beginning
      setVendors([]);
      setCurrentPage(1);
      setHasMore(true);
      refreshVendors();
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      showMessageModal(
        'Error',
        `Failed to delete vendor. Details: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  // Export to Excel (unchanged)
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(vendors);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
    XLSX.writeFile(wb, 'vendors.xlsx');
  };

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>
        {currentPage && lastPage && lastPage > 1
          ? `Loading vendors... (Page ${currentPage} of ${lastPage})`
          : 'Loading vendors...'}
      </span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Vendors Found</h5>
      <p className='mb-0'>
        There are currently no vendors in this list or matching your search
        criteria.
      </p>
    </div>
  );

  // Filtering/Pagination Logic (unchanged)
  const columns = [
    {
      name: 'S.No',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '3vw',
      compact: true,
      grow: 0,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Address',
      selector: (row) => row.address,
      sortable: true,
      minWidth: '12vw',
      wrap: true,
      grow: 2,
      style: {
        wordBreak: 'break-word',
      },
    },
    {
      name: 'Coordinates',
      selector: (row) =>
        row.lat !== null &&
        row.lat !== undefined &&
        row.lng !== null &&
        row.lng !== undefined
          ? `${(+row.lat).toFixed(4)}, ${(+row.lng).toFixed(4)}`
          : 'N/A',
      sortable: true,
      minWidth: '7vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Created',
      selector: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      minWidth: '7.5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Updated',
      selector: (row) => new Date(row.updated_at).toLocaleString(),
      sortable: true,
      minWidth: '7.5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Actions',
      center: true,
      cell: (row) => {
        const buttons = [
          <Button
            key='edit'
            size='sm'
            variant='warning'
            onClick={() => {
              setEditingVendor(row);
              setFormData({
                name: row.name,
                phone: row.phone,
                email: row.email,
                externalId: row.external_id,
                latitude: row.lat?.toString() || '',
                longitude: row.lng?.toString() || '',
                address: row.address || '',
              });
              const lat = parseFloat(row.lat);
              const lng = parseFloat(row.lng);
              if (!isNaN(lat) && !isNaN(lng)) {
                setMapCenter([lat, lng]);
              } else {
                setMapCenter(defaultCenter);
              }
              setShowModal(true);
            }}
            title='Edit vendor'
          >
            <FaEdit />
          </Button>,
          <Button
            key='delete'
            size='sm'
            variant='danger'
            onClick={() => handleDelete(row.id)}
            title='Delete vendor'
          >
            <FaTrash />
          </Button>,
        ];

        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {buttons.map((button) =>
              React.cloneElement(button, {
                style: {
                  ...button.props.style,
                  marginTop: '4px',
                  marginBottom: '4px',
                },
              })
            )}
          </div>
        );
      },
      minWidth: '6vw',
      grow: 0,
    },
  ];

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Vendor Management</h3>
        <div className='row justify-content-between align-items-center mt-3 mb-3'>
          <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <i className='fa fa-search'></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search Vendors'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
          <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
            <Button
              size='sm'
              variant='primary'
              onClick={() => {
                setEditingVendor(null);
                setFormData({
                  name: '',
                  phone: '',
                  email: '',
                  externalId: '',
                  latitude: '',
                  longitude: '',
                  address: '',
                });
                setShowModal(true);
              }}
              style={{ marginRight: '2px' }}
            >
              <i className='fa fa-plus'></i> New Vendor
            </Button>
            <Button size='sm' variant='success' onClick={exportToExcel}>
              <FaFileExport /> Export to Excel
            </Button>
          </div>
        </div>

        {/* Vendors Table */}
        <div style={{ maxHeight: '54vh', position: 'relative' }}>
          {/* Count Display */}
          {filteredVendors.length > 0 && (
            <div
              className='text-end p-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <small className='text-muted'>
                Showing <strong>{1}</strong> to{' '}
                <strong>{filteredVendors.length}</strong> out of{' '}
                <strong>{totalRows}</strong> vendors
              </small>
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredVendors}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='50vh'
            noTableHead={false}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {!loading && vendors.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>All vendors loaded ({vendors.length} total)</small>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditingVendor(null);
          }}
          size='lg'
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingVendor ? 'Edit Vendor' : 'New Vendor'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className='mb-3'>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-3'>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type='tel'
                      name='phone'
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className='mb-3'>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type='email'
                      name='email'
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-3'>
                    <Form.Label>External ID</Form.Label>
                    <Form.Control
                      type='text'
                      name='externalId'
                      value={formData.externalId}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Coordinates Input */}
              <Row className='align-items-center mb-3'>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      name='latitude'
                      value={formData.latitude}
                      onChange={handleChange}
                      type='number'
                      step='any'
                      placeholder='Enter latitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className='mb-2'>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      name='longitude'
                      value={formData.longitude}
                      onChange={handleChange}
                      type='number'
                      step='any'
                      placeholder='Enter longitude'
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className='mt-4'>
                  <Button
                    className='w-100'
                    variant='success'
                    onClick={handleOpenMap}
                  >
                    📍 Select on Map
                  </Button>
                </Col>
              </Row>

              <Form.Group className='mb-3'>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as='textarea'
                  name='address'
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </Form.Group>
              <Button variant='primary' type='submit' style={{ width: '100%' }}>
                {editingVendor ? 'Update Vendor' : 'Add Vendor'}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Map Popup (Now using the imported component) */}
        <MapPopup
          show={showMapPopup}
          handleClose={() => setShowMapPopup(false)}
          title='Select Vendor Location'
          center={mapCenter}
          setCoordinatesAndAddress={handleSelectLocation}
          mapLanguage={mapLanguage} // Pass the language state to the map component
        />

        {/* Message Modal (unchanged) */}
        <Modal
          show={messageModal.show}
          onHide={handleCloseMessageModal}
          centered
          size='md'
          className='text-center'
        >
          <Modal.Body
            className='py-4'
            style={{
              backgroundColor: '#ffffff',
              color: '#1f1f1f',
              borderRadius: '10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div className='mb-3'>
              {messageModal.title === 'Success' ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='green'
                  className='bi bi-check-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
                </svg>
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='60'
                  height='60'
                  fill='red'
                  className='bi bi-x-circle-fill'
                  viewBox='0 0 16 16'
                >
                  <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
                </svg>
              )}
            </div>
            <h5 className='mb-2'>{messageModal.title}</h5>
            <p>{messageModal.body}</p>
            <Button
              variant={messageModal.title === 'Success' ? 'success' : 'danger'}
              style={{
                width: '100%',
                fontWeight: '600',
                borderRadius: '10px',
                background:
                  messageModal.title === 'Success'
                    ? 'linear-gradient(135deg, #28a745, #198754)'
                    : 'linear-gradient(135deg, #dc3545, #b02a37)',
                border: 'none',
                boxShadow: '0px 4px 10px rgba(0,0,0,0.25)',
              }}
              onClick={handleCloseMessageModal}
            >
              Close
            </Button>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminVendor;
